<template>
  <div class="bg-gray-100 min-h-screen p-4 sm:p-6 lg:p-8">
    <div class="max-w-screen-xl mx-auto">
      <header class="mb-8 text-center">
        <router-link to="/super-admin" class="inline-block"><img class="h-16 w-auto" src="@/assets/logo.png" alt="Castive Logo"></router-link>
      </header>
      <main>
        <div class="bg-white rounded-xl shadow-lg p-6 sm:p-8">
          <div class="flex justify-between items-center border-b pb-4">
            <h1 class="text-2xl font-bold text-gray-800">Edit Client</h1>
            <router-link to="/super-admin" class="text-sm font-medium text-brand-purple-dark hover:text-brand-pink-static">&larr; Back to Super Admin</router-link>
          </div>
          <div v-if="loading" class="text-center py-10">Loading client data...</div>
          <div v-else-if="error" class="text-center py-10 text-red-600">{{ error }}</div>
          
          <div v-else-if="client" class="max-w-3xl mx-auto">
            <form @submit.prevent="handleUpdateClient" class="mt-6 space-y-6">
              
              <h3 class="text-lg font-medium text-gray-900">Podcast Details</h3>

              <div>
                <label for="podcastName" class="block text-sm font-medium text-gray-700">Podcast Name</label>
                <input v-model="client.podcastName" type="text" id="podcastName" required class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-brand-orange focus:border-brand-orange">
              </div>
              <div>
                <label for="podcastSlogan" class="block text-sm font-medium text-gray-700">Podcast Slogan</label>
                <input v-model="client.podcastSlogan" type="text" id="podcastSlogan" required class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-brand-orange focus:border-brand-orange">
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700">Podcast Icon Logo</label>
                <div class="mt-2 flex items-center">
                  <img v-if="logoPreviewUrl" :src="logoPreviewUrl" class="h-16 w-16 object-cover rounded-md mr-4">
                  <span v-else class="h-16 w-16 bg-gray-100 rounded-md mr-4 flex items-center justify-center text-xs text-gray-400">No Logo</span>
                  <input @change="handleFileSelect" type="file" id="logoFile" class="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-orange-50 file:text-brand-orange hover:file:bg-orange-100">
                </div>
              </div>
              <h3 class="text-lg font-medium text-gray-900 border-t pt-6">Branding</h3>
              <div>
                <label for="brandColour" class="block text-sm font-medium text-gray-700">Primary Brand Colour</label>
                <p class="text-xs text-gray-500 mb-2">Used for buttons and highlights on the client's dashboard.</p>
                <div class="flex items-center space-x-3">
                  <input v-model="client.brandColour" type="color" id="brandColour" class="h-10 w-14 p-1 border border-gray-300 rounded-md">
                  <input v-model="client.brandColour" type="text" class="block w-full border-gray-300 rounded-md shadow-sm focus:ring-brand-orange focus:border-brand-orange" placeholder="# Hex Code">
                </div>
              </div>
              <h3 class="text-lg font-medium text-gray-900 border-t pt-6">Client Admin</h3>
              <div v-if="adminUser">
                <label for="adminEmail" class="block text-sm font-medium text-gray-700">Admin Email Address</label>
                <input v-model="adminUser.email" type="email" id="adminEmail" required class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-brand-orange focus:border-brand-orange">
              </div>
              <div class="pt-4">
                <button type="submit" :disabled="isSaving" class="w-full bg-brand-orange text-white py-2 px-4 rounded-md hover:opacity-90 disabled:opacity-50">
                  <span v-if="isSaving">Saving...</span><span v-else>Save Changes</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>
      <footer class="mt-12 text-center py-4 space-y-4">
        <div class="flex items-center justify-center space-x-2"><img src="@/assets/logo.png" alt="Castive Logo" class="h-6 w-auto opacity-75"><p class="text-sm text-gray-600 font-medium">Powered by Castive</p></div>
        <div class="md:hidden"><a href="https://mannmadecreations.co.uk" target="_blank" rel="noopener noreferrer" class="pixel-perfect-link">GET PIXEL PERFECTION</a></div>
      </footer>
    </div>
  </div>
</template>

<script>
import { db, storage } from '@/firebase';
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { v4 as uuidv4 } from 'uuid';

export default {
  name: 'EditClientView',
  data() {
    return {
      client: null, loading: true, error: null, isSaving: false,
      logoFile: null,
      logoPreviewUrl: '',
      adminUser: null, originalAdminEmail: '',
    }
  },
  methods: {
    handleFileSelect(event) {
      const file = event.target.files[0];
      if (!file) return;
      this.logoFile = file;
      this.logoPreviewUrl = URL.createObjectURL(file);
    },
    async handleUpdateClient() {
      this.isSaving = true;
      try {
        const clientId = this.$route.params.id;
        if (this.adminUser.email !== this.originalAdminEmail) {
          const functions = getFunctions(undefined, 'europe-west2');
          const updateClientAdmin = httpsCallable(functions, 'updateClientAdmin');
          await updateClientAdmin({ clientId: clientId, adminUserId: this.adminUser.id, newEmail: this.adminUser.email });
        }

        let newLogoUrl = this.client.logoUrl;
        if (this.logoFile) {
          // --- START: DEFINITIVE FIX FOR UPLOAD PATH ---
          const fileExtension = this.logoFile.name.split('.').pop();
          const fileName = `client-logos/${clientId}/${uuidv4()}.${fileExtension}`;
          const storageRef = ref(storage, fileName);
          // --- END: DEFINITIVE FIX FOR UPLOAD PATH ---
          await uploadBytes(storageRef, this.logoFile);
          newLogoUrl = await getDownloadURL(storageRef);
        }
        
        const clientRef = doc(db, 'clients', clientId);
        await updateDoc(clientRef, {
          podcastName: this.client.podcastName,
          podcastSlogan: this.client.podcastSlogan,
          brandColour: this.client.brandColour || null,
          logoUrl: newLogoUrl,
          primaryContactEmail: this.adminUser.email,
        });
        
        alert('Client details updated successfully!');
        this.$router.push('/super-admin');
      } catch (error) {
        console.error("Error updating client:", error);
        alert(`Failed to update client details: ${error.message}`);
      } finally {
        this.isSaving = false;
      }
    }
  },
  async created() {
    try {
      const clientId = this.$route.params.id;
      if (!clientId) throw new Error("No client ID provided.");
      
      const clientRef = doc(db, 'clients', clientId);
      const docSnap = await getDoc(clientRef);
      if (!docSnap.exists()) throw new Error("Client not found.");
      
      const data = docSnap.data();
      data.brandColour = data.brandColour || data.brandColor || '#E7643B';
      this.client = data;
      this.logoPreviewUrl = data.logoUrl || '';
      
      const usersQuery = query(collection(db, 'users'), where('clientId', '==', clientId), where('role', '==', 'admin'));
      const querySnapshot = await getDocs(usersQuery);
      if (!querySnapshot.empty) {
        const adminDoc = querySnapshot.docs[0];
        this.adminUser = { id: adminDoc.id, ...adminDoc.data() };
        this.originalAdminEmail = this.adminUser.email;
      } else {
        throw new Error("Could not find an admin user for this client.");
      }
    } catch (err) {
      this.error = err.message;
    } finally {
      this.loading = false;
    }
  }
}
</script>